import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { accountService, type AccountProfile } from '../../services/account.service';
import { FONT_MONO, FONT_SERIF, IMG } from '../landing/landingData';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { useToast } from '../../components/common/Toast';

/**
 * Profile panel (design "HyVay Account" → Profile): photo, name, business name,
 * read-only email + login method, and the delete-account danger zone.
 *
 * Email stays read-only because it is the login identity (and the unique key),
 * so changing it needs a verification flow that does not exist yet.
 */

/** Display names for the stored `provider` value (the last login method). The
 *  stored value stays 'local' — it is referenced by server logic and existing
 *  rows — only the label shown to the user changes. */
const PROVIDER_LABEL: Record<string, string> = {
    local: 'Standard login',
    google: 'Google',
    github: 'GitHub',
};
const PROVIDER_NOTE: Record<string, string> = {
    local: 'You sign in with your email and password.',
    google: 'Signed in with Google — no password to remember.',
    github: 'Signed in with GitHub — no password to remember.',
};

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);
const labelStyle: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, letterSpacing: '-0.005em' };
const inputStyle: React.CSSProperties = { height: 46, padding: '0 16px', borderRadius: 14, border: '1px solid var(--hv-line)', background: 'var(--hv-page-2)', fontSize: 14, color: 'var(--hv-ink)', outline: 'none', fontFamily: 'inherit', width: '100%' };
const readonlyStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, height: 46, padding: '0 16px', borderRadius: 14, border: '1px dashed var(--hv-line-2)', background: 'var(--hv-card-2)', fontSize: 14, color: 'var(--hv-body)', minWidth: 0 };
const hint: React.CSSProperties = { fontSize: 11.5, lineHeight: 1.5, color: 'var(--hv-muted)' };

const ProviderIcon: React.FC<{ provider: string }> = ({ provider }) => {
    if (provider === 'google') {
        return <svg width="15" height="15" viewBox="0 0 24 24" style={{ flex: 'none' }}><path fill="#4285F4" d="M21.6 12.2c0-.7-.06-1.36-.18-2H12v3.79h5.4a4.62 4.62 0 0 1-2 3.03v2.5h3.22c1.88-1.73 2.98-4.28 2.98-7.32z" /><path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.22-2.5c-.9.6-2.05.95-3.4.95-2.62 0-4.84-1.77-5.63-4.15H3.06v2.6A9.99 9.99 0 0 0 12 22z" /><path fill="#FBBC05" d="M6.37 13.87a6 6 0 0 1 0-3.83V7.44H3.06a10 10 0 0 0 0 9.03z" /><path fill="#EA4335" d="M12 6.02c1.47 0 2.79.51 3.83 1.5l2.85-2.85C16.95 3.06 14.7 2 12 2 8.06 2 4.66 4.26 3.06 7.44l3.31 2.6C7.16 7.79 9.38 6.02 12 6.02z" /></svg>;
    }
    if (provider === 'github') {
        return <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }}><path d="M12 2.2A9.8 9.8 0 0 0 8.9 21.3c.5.1.7-.2.7-.5v-1.9c-2.7.6-3.3-1.3-3.3-1.3-.4-1.1-1.1-1.4-1.1-1.4-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.7.4-1.1.6-1.4-2.2-.3-4.5-1.1-4.5-4.9 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.7 0 0 .8-.3 2.7 1a9.3 9.3 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.4.2 2.4.1 2.7.6.7 1 1.6 1 2.7 0 3.8-2.3 4.6-4.5 4.9.4.3.7.9.7 1.9v2.8c0 .3.2.6.7.5A9.8 9.8 0 0 0 12 2.2z" /></svg>;
    }
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" style={{ flex: 'none', color: 'var(--hv-muted)' }}><rect x="5" y="10.5" width="14" height="9.5" rx="2" /><path d="M8.4 10.5V8a3.6 3.6 0 0 1 7.2 0v2.5" /></svg>;
};

export const ProfileSettingsView: React.FC = () => {
    const navigate = useNavigate();
    const { logout, refreshUser } = useAuth();
    const [profile, setProfile] = React.useState<AccountProfile | null>(null);
    const [fullName, setFullName] = React.useState('');
    const [company, setCompany] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const fileRef = React.useRef<HTMLInputElement>(null);

    const confirm = useConfirm();
    const toast = useToast();

    const load = React.useCallback(async () => {
        setLoading(true);
        const res = await accountService.getProfile();
        if (res.success && res.data) {
            setProfile(res.data);
            setFullName(res.data.fullName ?? '');
            setCompany(res.data.company ?? '');
        } else {
            setError((res as any).message || 'Could not load your profile.');
        }
        setLoading(false);
    }, []);

    React.useEffect(() => { void load(); }, [load]);

    const dirty = !!profile && (fullName !== (profile.fullName ?? '') || company !== (profile.company ?? ''));

    const save = async () => {
        if (!dirty) return;
        setSaving(true);
        setError(null);
        const res = await accountService.updateProfile({ fullName, company });
        if (res.success && res.data) {
            setProfile((p) => (p ? { ...p, ...res.data } : p));
            setSaved(true);
            setTimeout(() => setSaved(false), 2600);
            void refreshUser();
        } else {
            setError((res as any).message || 'Could not save your changes.');
        }
        setSaving(false);
    };

    const discard = () => {
        setFullName(profile?.fullName ?? '');
        setCompany(profile?.company ?? '');
        setSaved(false);
    };

    const pickPhoto = async (file: File) => {
        setUploading(true);
        setError(null);
        const res = await accountService.uploadAvatar(file);
        if (res.success && res.data) {
            setProfile((p) => (p ? { ...p, avatar: res.data!.avatar } : p));
            void refreshUser();
        } else {
            setError((res as any).message || 'Could not upload the photo.');
        }
        setUploading(false);
    };

    const requestDeleteAccount = async () => {
        if (!profile) return;
        const ok = await confirm({
            title: <>Delete your <em>account</em></>,
            description: <>Every project, live site, upload and remaining credit is removed permanently. Credits are not refunded. To confirm, type <strong style={{ color: 'var(--hv-ink)' }}>{profile.email}</strong> below.</>,
            typeToConfirm: profile.email,
            confirmLabel: 'Delete everything',
            cancelLabel: 'Keep my account',
            maxWidth: 452,
            onConfirm: async (typed) => {
                const res = await accountService.deleteAccount(typed);
                if (!res.success) throw new Error((res as any).message || 'Could not delete the account. Please try again.');
            },
        });
        if (!ok) return;
        logout();
        navigate('/', { replace: true });
        toast.success('Your account has been deleted.');
    };

    const provider = profile?.provider ?? 'local';
    const nameShown = (fullName || profile?.fullName || '').trim() || profile?.email || 'Your profile';

    if (loading) {
        return (
            <div className="hv-panel" style={{ padding: 30, borderRadius: 24, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.16em', color: 'var(--hv-muted)' }}>LOADING PROFILE…</div>
        );
    }

    return (
        <>
            <div className="hv-panel" style={{ padding: 30, borderRadius: 24, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 26px 50px -40px var(--hv-shadow)', animation: 'hvFade 0.4s ease both' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18, paddingBottom: 26, borderBottom: '1px solid var(--hv-line)' }}>
                    <div style={{ position: 'relative', flex: 'none' }}>
                        <img src={profile?.avatar || IMG.avatarDefault} alt="" style={{ width: 78, height: 78, borderRadius: 999, objectFit: 'cover', border: '1px solid var(--hv-line)', display: 'block' }} />
                        <button type="button" aria-label="Change photo" disabled={uploading} onClick={() => fileRef.current?.click()} className="hv-h-leaf" style={{ position: 'absolute', right: -2, bottom: -2, width: 28, height: 28, borderRadius: 999, border: '2px solid var(--hv-card)', background: 'var(--brand,#13422C)', color: '#FFFDF7', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, opacity: uploading ? 0.7 : 1 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8.6h3l1.4-2h7.2l1.4 2h3v10H4z" /><circle cx="12" cy="13.4" r="3" /></svg>
                        </button>
                        <input ref={fileRef} type="file" accept="image/*,.heic,.heif" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickPhoto(f); e.target.value = ''; }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: FONT_SERIF, fontSize: 29, lineHeight: 1.1, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameShown}</div>
                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--hv-muted)' }}>JPG, PNG, WebP, GIF or HEIC · up to 5 MB</div>
                    </div>
                    <div style={{ flex: 1 }} />
                </div>

                <div className="hv-2col" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '18px 20px', marginTop: 26, maxWidth: 400 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        <span style={labelStyle}>Full name</span>
                        <input className="hv-input" value={fullName} onChange={(e) => { setFullName(e.target.value); setSaved(false); }} placeholder="Your name" style={inputStyle} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        <span style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>Business name
                            <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 400, letterSpacing: '0.12em', color: 'var(--hv-muted)' }}>OPTIONAL</span>
                        </span>
                        <input className="hv-input" value={company} onChange={(e) => { setCompany(e.target.value); setSaved(false); }} placeholder="Kopi Rakyat" style={inputStyle} />
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        <span style={labelStyle}>Email</span>
                        <span style={readonlyStyle}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" style={{ flex: 'none', color: 'var(--hv-muted)' }}><rect x="5" y="10.5" width="14" height="9.5" rx="2" /><path d="M8.4 10.5V8a3.6 3.6 0 0 1 7.2 0v2.5" /></svg>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile?.email}</span>
                        </span>
                        <span style={hint}>Your email is how you log in, so it can't be changed here.</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        <span style={labelStyle}>Login method</span>
                        <span style={readonlyStyle}>
                            <ProviderIcon provider={provider} />
                            {PROVIDER_LABEL[provider] ?? provider}
                        </span>
                        <span style={hint}>{PROVIDER_NOTE[provider] ?? 'This is the method you used to sign in last.'}</span>
                    </div>
                </div>

                {error && (
                    <div role="alert" style={{ marginTop: 18, padding: '12px 14px', borderRadius: 14, background: 'var(--hv-danger-bg)', color: 'var(--hv-danger)', fontSize: 13, fontWeight: 600, lineHeight: 1.55 }}>{error}</div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 26, paddingTop: 22, borderTop: '1px solid var(--hv-line)' }}>
                    <button type="button" onClick={() => void save()} disabled={!dirty || saving} className="hv-h-brand-82" style={{ display: 'flex', alignItems: 'center', height: 46, padding: '0 24px', borderRadius: 999, border: 0, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontSize: 14, fontWeight: 700, cursor: !dirty || saving ? 'default' : 'pointer', opacity: !dirty || saving ? 0.6 : 1 }}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                    <button type="button" onClick={discard} disabled={!dirty} className="hv-h-page2" style={{ display: 'flex', alignItems: 'center', height: 46, padding: '0 22px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 14, fontWeight: 700, cursor: dirty ? 'pointer' : 'default', opacity: dirty ? 1 : 0.6 }}>Discard</button>
                    {saved && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--hv-mint-ink)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.8l4.6 4.4L19 7" /></svg>
                            Saved
                        </span>
                    )}
                </div>
            </div>

            {/* danger zone */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: '18px 22px', borderRadius: 18, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.005em', color: 'var(--hv-ink)' }}>Delete account</div>
                    <div style={{ marginTop: 3, fontSize: 12.5, lineHeight: 1.55, color: 'var(--hv-muted)', ...tw('pretty') }}>Removes every project, upload and credit for good.</div>
                </div>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => void requestDeleteAccount()} className="hv-h-danger" style={{ flex: 'none', height: 38, padding: '0 18px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-danger)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
            </div>

        </>
    );
};
